import { useEffect } from 'react';
import classNames from 'classnames';
import CamAdPlayer from 'components/CamAdPlayer';
import VideoJsAdScripts from 'components/VideoJsAdScripts';
import { useReelPlayerAds } from 'hooks/useReelPlayerAds';
import { Camera } from 'types/camera';
import { SpotDetails } from 'types/spot';

import styles from './ReelPlayerAds.module.scss';

const ReelPlayerAds = ({
  camera,
  playerPlaying,
  currentReelIndex,
  shouldCountAsClipPlayed,
  shouldResetClipsPlayedCount,
  onAdRunningChange,
  onPrerollResolvedChange,
  onPrerollSkippedChange,
  onInitAdContainerReady,
  spot,
  prerollEnabled = false,
}: {
  camera: Camera;
  playerPlaying: boolean;
  currentReelIndex: number;
  onAdRunningChange: (adRunning: boolean) => void;
  onPrerollResolvedChange?: (resolved: boolean) => void;
  onPrerollSkippedChange?: (skipped: boolean) => void;
  onInitAdContainerReady?: (initAdContainer: () => void) => void;
  shouldCountAsClipPlayed: boolean;
  shouldResetClipsPlayedCount: boolean;
  spot: SpotDetails;
  prerollEnabled?: boolean;
}) => {
  const {
    adRunning,
    adSettings,
    videoAdPluginsLoaded,
    onVideoJsGoogleImaLoaded,
    onVideoJsGoogleImaError,
    setPlayAd,
    setInitAdContainer,
    setAdRunning,
    shouldShowAdPlayer,
    prerollResolved,
    prerollSkipped,
    initAdContainer,
  } = useReelPlayerAds({
    camera,
    playerPlaying,
    currentReelIndex,
    shouldCountAsClipPlayed,
    shouldResetClipsPlayedCount,
    prerollEnabled,
  });

  // We hoist up the adRunning state to disable the next/previous buttons when an ad is playing
  useEffect(() => {
    onAdRunningChange(adRunning);
  }, [adRunning, onAdRunningChange]);

  useEffect(() => {
    onPrerollResolvedChange?.(prerollResolved);
  }, [prerollResolved, onPrerollResolvedChange]);

  useEffect(() => {
    onPrerollSkippedChange?.(prerollSkipped);
  }, [prerollSkipped, onPrerollSkippedChange]);

  useEffect(() => {
    onInitAdContainerReady?.(initAdContainer);
  }, [initAdContainer, onInitAdContainerReady]);

  return (
    <>
      <VideoJsAdScripts
        hasAdFreeCamPermissions={false}
        onVideoJsGoogleImaError={onVideoJsGoogleImaError}
        onVideoJsGoogleImaLoaded={onVideoJsGoogleImaLoaded}
      />
      {shouldShowAdPlayer && (
        <div
          className={classNames({
            [styles.hidden]: !adRunning,
            [styles.camAdPlayer]: true,
          })}
          data-testid={adRunning ? 'ad-player' : undefined}
        >
          <CamAdPlayer
            // Remount per cam so IMA refs and the playAd registration restart cleanly on cam-angle switch.
            key={camera?._id}
            adSettings={adSettings}
            hasAdFreeCamPermissions={false}
            segmentScreen="reel-player"
            setPlayAd={setPlayAd}
            setInitAdContainer={setInitAdContainer}
            setAdRunning={setAdRunning}
            spot={spot}
            videoAdPluginsLoaded={videoAdPluginsLoaded}
          />
        </div>
      )}
    </>
  );
};

export default ReelPlayerAds;
